import { FC, PropsWithChildren } from "react";
import { t } from "@lingui/macro";
import { Box, Flex, BoxProps } from "@otrium/core";
import { AndroidIcon } from "src/icons/AndroidIcon";
import { AppleIcon } from "src/icons/AppleIcon";
import { InstaIcon } from "src/icons/InstaIcon";
import { locale } from "src/config/app-config";
import { appStoreUrl, googlePlayUrl } from "src/utils/constants";
import {
  APP_DOWNLOAD_ORIGIN,
  APP_DOWNLOAD_STORE,
  AppDownloadStore,
  trackAppDownloadButtonClickedEvent,
} from "src/tracking/events/app-download-button-clicked";
import { useIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { EmotionBaseProps as BaseProps } from "src/types/global";
import styles from "./Footer.module.scss";
import { TiktokIcon } from "src/icons/TiktokIcon";
import { ButtonProps } from "src/atoms/Button";
import { Styled } from "src/molecules/Footer/FooterButtons.styled";
import { useLingui } from "@lingui/react";

const CustomButton: FC<PropsWithChildren<ButtonProps>> = ({
  children,
  ...props
}) => (
  <Styled.FooterButton
    as="a"
    target="_blank"
    rel="noopener noreferrer"
    {...props}
  >
    {children}
  </Styled.FooterButton>
);

const AppDownloadButton: FC<
  PropsWithChildren<ButtonProps & { href: string; store: AppDownloadStore }>
> = ({ href, store, children, ...props }) => (
  <CustomButton
    href={href}
    onClick={() =>
      void trackAppDownloadButtonClickedEvent({
        origin: APP_DOWNLOAD_ORIGIN.footer,
        store,
        destinationUrl: href,
      })
    }
    {...props}
  >
    {children}
  </CustomButton>
);

type ButtonBoxType = BoxProps &
  BaseProps & {
    label: string;
  };

const ButtonBox: FC<PropsWithChildren<ButtonBoxType>> = ({
  children,
  label,
  ...rest
}) => (
  <Box {...rest}>
    <h4 className={styles.title}>{label}</h4>
    <Flex variant="centered">{children}</Flex>
  </Box>
);

export const FooterButtons: FC<PropsWithChildren<unknown>> = () => {
  const isLoggedIn = useIsLoggedIn();
  const { i18n } = useLingui();
  return (
    <Box
      width={["100%", "100%", "100%", isLoggedIn ? "25%" : "calc(100% / 3)"]}
      mt={[5, 5, 5, "48px"]}
    >
      <ButtonBox label={t(i18n)`Download our app`}>
        <AppDownloadButton
          data-testid="app-store-badge"
          href={appStoreUrl(locale)}
          store={APP_DOWNLOAD_STORE.appStore}
        >
          <AppleIcon />
        </AppDownloadButton>
        <AppDownloadButton
          data-testid="google-play-badge"
          href={googlePlayUrl(locale)}
          store={APP_DOWNLOAD_STORE.googlePlay}
        >
          <AndroidIcon />
        </AppDownloadButton>
      </ButtonBox>
      <ButtonBox label={t(i18n)`Get social`} mt={[5, 5, 5, 3]}>
        <CustomButton href="https://www.tiktok.com/@otrium" aria-label="Tiktok">
          <TiktokIcon />
        </CustomButton>
        <CustomButton
          href="https://www.instagram.com/otriumofficial/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <InstaIcon />
        </CustomButton>
      </ButtonBox>
      <Box mt={"36px"}>
        <iframe
          role="none"
          title="Facebook plugin"
          src="https://www.facebook.com/plugins/like.php?href=https%3A%2F%2Fwww.facebook.com%2Fotriumofficial%2F&width=141&layout=button_count&action=like&size=small&share=true&height=46&appId"
          scrolling="no"
          frameBorder="0"
          allow="encrypted-media"
          style={{
            height: "20" + "px",
            width: 100 + "%", // eslint-disable-line @typescript-eslint/restrict-plus-operands
            border: "none",
            overflow: "hidden",
          }}
        />
      </Box>
    </Box>
  );
};
